import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/auth';

/**
 * Welle 8d — Magic-Link: Berater-seitiges Overlay für eingehende Beitrittsanfragen
 * eines Gasts/Experten. Erscheint während eines laufenden Calls (kein View-Wechsel),
 * getrieben durch das Custom-Event 'bamoa:join-request' aus useAgentSocket.
 *
 * Die Freigabe durch den Berater ist das eigentliche Tor — der Gast bekommt seinen
 * rtcToken erst nach 'admit'. Der angezeigte Name ist behauptet (nicht verifiziert),
 * daher als „angegeben" gekennzeichnet; bei Vorab-Links zusätzlich der erwartete Name.
 */
interface JoinRequest {
  requestId: string;
  callId: string;
  name: string;
  expectedName?: string;
  tenantId: string;
}

export function JoinRequestPrompt() {
  const [req, setReq] = useState<JoinRequest | null>(null);
  const [busy, setBusy] = useState<'admit' | 'decline' | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    const onReq = (e: Event) => {
      const detail = (e as CustomEvent).detail as JoinRequest;
      setErr('');
      setBusy(null);
      setReq(detail);
    };
    // Welle 12 (pre125): woanders zugelassen/abgelehnt → dieses Overlay schließen.
    const onResolved = (e: Event) => {
      const d = (e as CustomEvent).detail as { requestId?: string };
      setReq((cur) => (cur && d?.requestId === cur.requestId ? null : cur));
    };
    window.addEventListener('bamoa:join-request', onReq);
    window.addEventListener('bamoa:join-resolved', onResolved);
    return () => {
      window.removeEventListener('bamoa:join-request', onReq);
      window.removeEventListener('bamoa:join-resolved', onResolved);
    };
  }, []);

  if (!req) return null;

  const act = async (kind: 'admit' | 'decline') => {
    setBusy(kind);
    setErr('');
    try {
      const res = await apiFetch(`/api/links/join/${encodeURIComponent(req.requestId)}/${kind}`, {
        method: 'POST',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErr(body?.message || 'Aktion fehlgeschlagen.');
        setBusy(null);
        return;
      }
      setReq(null);
    } catch {
      setErr('Verbindung fehlgeschlagen.');
      setBusy(null);
    }
  };

  return (
    <div className="jr-overlay" role="dialog" aria-label="Beitrittsanfrage">
      <style>{JR_CSS}</style>
      <div className="jr-card">
        <div className="jr-pulse">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M19 8v6M22 11h-6" />
          </svg>
        </div>
        <div className="jr-title">Beitrittsanfrage</div>
        <div className="jr-name">{req.name}</div>
        <div className="jr-meta">möchte dem Gespräch beitreten (Name angegeben)</div>
        {req.expectedName && (
          <div className="jr-expected">
            erwartet: <strong>{req.expectedName}</strong>
          </div>
        )}
        {err && <div className="jr-err">{err}</div>}
        <div className="jr-actions">
          <button className="jr-btn jr-decline" disabled={busy !== null} onClick={() => act('decline')}>
            {busy === 'decline' ? '…' : 'Ablehnen'}
          </button>
          <button className="jr-btn jr-admit" disabled={busy !== null} onClick={() => act('admit')}>
            {busy === 'admit' ? '…' : 'Zulassen'}
          </button>
        </div>
      </div>
    </div>
  );
}

const JR_CSS = `
.jr-overlay {
  position: fixed; inset: 0; z-index: 70;
  display: flex; align-items: center; justify-content: center;
  background: rgba(4,12,14,0.55); backdrop-filter: blur(3px);
}
.jr-card {
  width: 340px; max-width: calc(100vw - 40px);
  background: #0e2a2f; border: 1px solid rgba(95,212,196,0.25);
  border-radius: 18px; padding: 26px 24px 22px;
  text-align: center; color: #e8f1f1;
  box-shadow: 0 24px 60px rgba(0,0,0,0.5);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.jr-pulse {
  width: 60px; height: 60px; margin: 0 auto 12px;
  border-radius: 50%; background: rgba(95,212,196,0.14); color: #5fd4c4;
  display: flex; align-items: center; justify-content: center;
  animation: jr-pulse 1.6s ease-in-out infinite;
}
.jr-pulse svg { width: 28px; height: 28px; }
@keyframes jr-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(95,212,196,0.30); }
  50% { box-shadow: 0 0 0 12px rgba(95,212,196,0); }
}
.jr-title { font-size: 12px; letter-spacing: 1.5px; text-transform: uppercase; color: #7fa6a3; }
.jr-name { font-size: 22px; font-weight: 680; margin-top: 6px; }
.jr-meta { font-size: 13.5px; color: #aecccb; margin-top: 4px; line-height: 1.4; }
.jr-expected { font-size: 13px; color: #7fa6a3; margin-top: 8px; }
.jr-err { font-size: 13px; color: #f0a17f; margin-top: 10px; }
.jr-actions { display: flex; gap: 10px; margin-top: 20px; }
.jr-btn {
  flex: 1; padding: 13px 0; font-size: 15px; font-weight: 650;
  border-radius: 11px; border: none; cursor: pointer; transition: opacity 0.15s, background 0.15s;
}
.jr-btn:disabled { opacity: 0.5; cursor: default; }
.jr-decline { background: rgba(255,255,255,0.08); color: #e8f1f1; }
.jr-decline:hover:not(:disabled) { background: rgba(255,255,255,0.14); }
.jr-admit { background: #2bb6a3; color: #042; }
.jr-admit:hover:not(:disabled) { background: #36c9b5; }
`;
